import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type { LiveRankingsCategory } from '../api/liveRankings';

const ONE_HOUR_MS = 60 * 60 * 1000;
const STORAGE_NAME = 'live-rankings-category';

export type LiveRankingCategoryFilter = 'all' | LiveRankingsCategory;

interface LiveRankingsCategoryState {
    categoryFilter: LiveRankingCategoryFilter;
    persistedAt: number;
    setCategoryFilter: (category: LiveRankingCategoryFilter) => void;
}

/** Drops persisted payload when older than 1 hour (since last category change). */
const expiringLocalStorage: StateStorage = {
    getItem: name => {
        const str = localStorage.getItem(name);
        if (!str) return null;
        try {
            const parsed = JSON.parse(str) as { state?: { persistedAt?: number } };
            const at = parsed?.state?.persistedAt;
            if (typeof at === 'number' && Date.now() - at > ONE_HOUR_MS) {
                localStorage.removeItem(name);
                return null;
            }
            return str;
        } catch {
            return null;
        }
    },
    setItem: (name, value) => {
        localStorage.setItem(name, value);
    },
    removeItem: name => {
        localStorage.removeItem(name);
    },
};

export const useLiveRankingsCategoryStore = create<LiveRankingsCategoryState>()(
    persist(
        set => ({
            categoryFilter: 'all',
            persistedAt: Date.now(),
            setCategoryFilter: categoryFilter => set({ categoryFilter, persistedAt: Date.now() }),
        }),
        {
            name: STORAGE_NAME,
            storage: createJSONStorage(() => expiringLocalStorage),
            partialize: state => ({
                categoryFilter: state.categoryFilter,
                persistedAt: state.persistedAt,
            }),
        }
    )
);
